import { useState } from 'react'
import { XMarkIcon, DocumentIcon } from '@heroicons/react/24/outline'
import { MultiSelect, MultiSelectItem } from '@tremor/react'
import { useTranslation } from 'react-i18next'

function EditSectionModal({ section, projectAnalyses, onSave, onClose }) {
    const { t } = useTranslation()
    const [title, setTitle] = useState(section.section_title)
    const [instructions, setInstructions] = useState(section.section_instructions)
    const [prompt, setPrompt] = useState(section.section_prompt || '')
    const [recommendedPages, setRecommendedPages] = useState(section.recommended_pages || '')
    const [selectedAnalyses, setSelectedAnalyses] = useState(section.files || [])

    const handleAnalysisChange = (values) => {
        setSelectedAnalyses(values)
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        onSave({
            section_title: title,
            section_instructions: instructions,
            section_prompt: prompt,
            recommended_pages: recommendedPages,
            analysis_uuids: selectedAnalyses
        })
    }

    const getAnalysisDisplayName = (analysis) => {
        return analysis.name || analysis.meeting_name || `Analysis ${analysis.uuid.slice(0, 8)}`
    }

    return (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium">{t('articles.editSectionModal.title')}</h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('articles.editSectionModal.sectionTitle')}<span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-black focus:border-transparent"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('articles.editSectionModal.instructions')}<span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            rows={3}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-black focus:border-transparent"
                            required
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('articles.editSectionModal.additionalPrompt')}
                        </label>
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            rows={2}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-black focus:border-transparent"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('articles.editSectionModal.expectedPages')}
                        </label>
                        <input
                            type="number"
                            value={recommendedPages}
                            onChange={(e) => setRecommendedPages(e.target.value)}
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-black focus:border-transparent"
                            placeholder={t('articles.editSectionModal.expectedPagesPlaceholder')}
                            min="0.01"
                            step="0.01"
                        />
                    </div>

                    {/* Analysis Selection */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('articles.editSectionModal.selectAnalysisFiles')}
                        </label>
                        
                        <MultiSelect 
                            onValueChange={handleAnalysisChange} 
                            value={selectedAnalyses || []} 
                            className="w-full" 
                            placeholder={t('articles.editSectionModal.selectAnalysisPlaceholder')}
                        >
                            {projectAnalyses?.filter(analysis => 
                                analysis.status === "Processed" && 
                                (analysis.file_type === "audio" || analysis.file_type === "manual_transcription" || analysis.file_type === "pdf" || analysis.file_type === "questionnaire")
                            ).map((analysis) => (
                                <MultiSelectItem 
                                    key={analysis.uuid} 
                                    value={analysis.uuid} 
                                    icon={DocumentIcon}
                                >
                                    {getAnalysisDisplayName(analysis)}
                                </MultiSelectItem>
                            ))}
                        </MultiSelect>
                        
                        <p className="mt-2 text-xs text-gray-500">
                            {t('articles.editSectionModal.selectedFiles', { 
                                selected: selectedAnalyses.length, 
                                total: projectAnalyses?.filter(analysis => 
                                    analysis.status === "Processed" && 
                                    (analysis.file_type === "audio" || analysis.file_type === "manual_transcription" || analysis.file_type === "pdf" || analysis.file_type === "questionnaire")
                                ).length || 0 
                            })}
                        </p>
                    </div>
                    
                    <div className="flex gap-3 justify-end pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        >
                            {t('articles.editSectionModal.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
                        >
                            {t('articles.editSectionModal.saveChanges')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default EditSectionModal